// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import Chart from "react-apexcharts"
import type { ApexOptions } from "apexcharts"
import { useParams } from "react-router-dom"
import { useCycleLatestSnapshot } from "@/queries/queries.eventJournal"
import { parseCycleSnapshotPayload } from "../_snapshotPayload"
import ChartZeroState from "../ChartZeroState"

// Agent-cost box-plot — iso with `ebiose-webapp/src/views/cycles/graph.agentCost.tsx`.
// Snapshot exposes per-agent cumulative `total_evaluation_cost` + `first_iter`/`last_iter` (PRD §M3).
// Agents are bucketed by `last_iter` (the iteration they finished); the 5-number summary is over
// agent lifetime cost, not strict per-iter spend — same shape as the legacy ApexCharts box-plot.
export interface AgentCostChartProps {
  cycleId?: string
}

interface AgentSnapshotPayload {
  iter_index: number
  per_agent_rollup: ReadonlyArray<{
    agent_uuid: string
    last_iter: number
    total_evaluation_cost: number
  }>
}

const APEX_OPTIONS: ApexOptions = {
  chart: {
    type: "boxPlot",
    height: 400,
    background: "transparent",
    toolbar: { show: false },
    animations: { enabled: false },
    zoom: { enabled: false },
  },
  theme: { mode: "dark" },
  xaxis: {
    type: "category",
    labels: { style: { colors: "#5f8c84" } },
    title: {
      text: "Iterations",
      style: { color: "#5f8c84", fontSize: "16px" },
    },
  },
  yaxis: {
    min: 0,
    title: {
      text: "Cost ($)",
      style: { color: "#5f8c84", fontSize: "16px" },
    },
    labels: {
      style: { colors: "#5f8c84" },
      formatter: (value: number) => `$${value.toFixed(2)}`,
    },
  },
  plotOptions: {
    boxPlot: {
      colors: {
        upper: "#45f0c8",
        lower: "#5b8def",
      },
    },
  },
  stroke: { colors: ["#2e6e72"] },
  grid: { borderColor: "#11393f" },
  tooltip: {
    theme: "dark",
    y: { formatter: (value: number) => `$${value.toFixed(2)}` },
  },
  legend: {
    show: true,
    itemMargin: { horizontal: 10, vertical: 5 },
    position: "right",
    labels: { colors: "#a9cfc6" },
    markers: { size: [0, 7] as unknown as number },
    formatter: (seriesName: string) =>
      seriesName === "Agent Costs" ? "" : seriesName,
  },
}

const percentileR7 = (sorted: readonly number[], p: number): number => {
  if (sorted.length === 0) return 0
  if (sorted.length === 1) return sorted[0]
  const rank = p * (sorted.length - 1)
  const lo = Math.floor(rank)
  const hi = Math.ceil(rank)
  if (lo === hi) return sorted[lo]
  const frac = rank - lo
  return sorted[lo] * (1 - frac) + sorted[hi] * frac
}

const fiveNumberSummary = (
  values: readonly number[],
): [number, number, number, number, number] | null => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  return [
    sorted[0],
    percentileR7(sorted, 0.25),
    percentileR7(sorted, 0.5),
    percentileR7(sorted, 0.75),
    sorted[sorted.length - 1],
  ]
}

export default function AgentCostChart({ cycleId }: AgentCostChartProps = {}) {
  const params = useParams<{ cycleId: string }>()
  const resolvedCycleId = cycleId ?? params.cycleId
  const snapshotQuery = useCycleLatestSnapshot(resolvedCycleId)

  // Render nothing while the snapshot read is in flight (no zero-state
  // flash before the source resolves).
  if (snapshotQuery.isLoading) {
    return null
  }

  // Per-panel gating (issue `per-panel-gating-t0`): once the read has
  // resolved without a snapshot row, the panel owns its own zero-state
  // (US §10).
  if (!snapshotQuery.isSuccess || snapshotQuery.data == null) {
    return <ChartZeroState message="No agent cost data yet." />
  }

  const payload = parseCycleSnapshotPayload<AgentSnapshotPayload>(
    snapshotQuery.data.payload_json,
  )
  if (payload === null) return null

  const rollup = payload.per_agent_rollup ?? []
  if (rollup.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center text-text-tertiary">
        No agent data yet.
      </div>
    )
  }

  const lastIter = payload.iter_index
  const buckets: number[][] = Array.from({ length: lastIter + 1 }, () => [])
  for (const agent of rollup) {
    const iter = agent.last_iter
    if (iter >= 0 && iter <= lastIter) {
      buckets[iter].push(agent.total_evaluation_cost)
    }
  }

  const boxPlotData = buckets
    .map((values, iter) => {
      const summary = fiveNumberSummary(values)
      if (summary === null) return null
      return { x: `${iter + 1}`, y: summary }
    })
    .filter(
      (
        point,
      ): point is {
        x: string
        y: [number, number, number, number, number]
      } => point !== null,
    )

  if (boxPlotData.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center text-text-tertiary">
        No agent cost data to plot.
      </div>
    )
  }

  const series = [
    {
      name: "Agent Costs",
      type: "boxPlot" as const,
      data: boxPlotData,
    },
  ]

  return (
    <div
      role="figure"
      aria-label="Agent evaluation cost distribution per iteration"
      className="w-full"
    >
      <Chart
        options={APEX_OPTIONS}
        series={series}
        type="boxPlot"
        height={400}
      />
    </div>
  )
}
